import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ClinicalTask,
  ClinicalTaskListRequest,
  ClinicalTaskListResponse,
  ClinicalTaskNote,
  ClinicalTaskRequest,
} from 'src/app/models/clients/clinical-task.model';
import { User } from 'src/app/models/user.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  baseUrl = `${environment.welbyEndpoint}/api/v1/core`;
  constructor(
    private fsService: FirestoreService,
    private http: HttpClient,
    private auth: NewAuthService
  ) {}

  saveTask(task: ClinicalTask): Observable<any> {
    return task.id ? this.updateTask(task) : this.createTask(task);
  }

  getNotesByTaskId(id: string, patientId: string): Observable<ClinicalTaskNote[]> {
    const url = `${this.baseUrl}/patients/${patientId}/clinical-tasks/${id}/notes`;
    return this.http.get<ClinicalTaskNote[]>(url);
  }

  saveTaskNote(note, taskId: string, patientId: string): Observable<{ success: boolean }> {
    const url = `${this.baseUrl}/patients/${patientId}/clinical-tasks/${taskId}/notes`;
    return this.http.post<{ success: boolean }>(url, note);
  }

  updateTaskNote(note, taskId: string, noteId: string, patientId: string): Observable<{ success: boolean }> {
    const url = `${this.baseUrl}/patients/${patientId}/clinical-tasks/${taskId}/notes/${noteId}`;
    return this.http.put<{ success: boolean }>(url, note);
  }

  deleteTaskNote(taskId: string, noteId: string, patientId: string): Observable<{ success: boolean }> {
    const url = `${this.baseUrl}/patients/${patientId}/clinical-tasks/${taskId}/notes/${noteId}`;
    return this.http.delete<{ success: boolean }>(url);
  }

  getPendingTasksByUser(user_id: string) {
    return this.fsService.colWithIds$('clinical_tasks', (ref) =>
      ref.where('assignee_id', '==', user_id).where('completed', '==', false)
    );
  }

  getTasksByPatient(patientId: string, searchTaskParams: ClinicalTaskRequest) {
    const url = `${this.baseUrl}/patients/${patientId}/clinical-tasks/search-by-params`;
    return this.http.post<ClinicalTaskListResponse>(url, searchTaskParams);
  }

  getTasksByClient(searchTaskParams: ClinicalTaskRequest): Observable<ClinicalTaskListResponse> {
    const url = `${this.baseUrl}/clinical-tasks/search-by-params`;
    return this.http.post<ClinicalTaskListResponse>(url, searchTaskParams);
  }

  getTasksByProvider(searchTaskParams: ClinicalTaskListRequest): Observable<ClinicalTaskListResponse> {
    const providerId = searchTaskParams.providerId;
    const url = `${this.baseUrl}/providers/${providerId}/clinical-tasks`;
    return this.http.post<ClinicalTaskListResponse>(url, searchTaskParams);
  }

  markTaskAsCompleted(clinicalTask: ClinicalTask): Observable<{ success: boolean }> {
    return this.updateTask({
      id: clinicalTask.id,
      patient_id: clinicalTask.patient_id,
      completed: true,
      completed_by: this.auth.user.id,
    });
  }

  markTaskAsUncompleted(clinicalTask: ClinicalTask): Observable<{ success: boolean }> {
    return this.updateTask({
      id: clinicalTask.id,
      patient_id: clinicalTask.patient_id,
      completed: false,
    });
  }

  getProviders(clientsId: string[]): Observable<User[]> {
    if (this.auth.user.roles.isWelbyClinical === true || this.auth.user.roles.isAdmin === true) {
      return this.fsService
        .col('users', (ref) => ref.where('roles.isWelbyClinical', '==', true).orderBy('firstName', 'asc'))
        .valueChanges();
    } else {
      return this.fsService
        .col('users', (ref) =>
          ref
            .where('client_responsible_id', 'in', clientsId)
            .where('roles.isClient', '==', true)
            .orderBy('firstName', 'asc')
        )
        .valueChanges();
    }
  }

  pinTask(task: ClinicalTask, providerId: string): Observable<{ success: boolean }> {
    const url = `${this.baseUrl}/providers/${providerId}/clinical-tasks/${task.id}/pin`;
    return this.http.put<{ success: boolean }>(url, {
      pinned: task.pinned,
      pinnedProperties: task.pinnedProperties,
    });
  }

  private createTask(task: ClinicalTask): Observable<{ success: boolean; taskId: string }> {
    delete task.id;
    const url = `${this.baseUrl}/patients/${task.patient_id}/clinical-tasks`;
    return this.http.post<{ success: boolean; taskId: string }>(url, task);
  }

  private updateTask(task: ClinicalTask): Observable<{ success: boolean }> {
    const url = `${this.baseUrl}/patients/${task.patient_id}/clinical-tasks/${task.id}`;
    return this.http.put<{ success: boolean }>(url, task);
  }
}
